import React, { useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { RootState } from 'src/reducers';
import { Consent, Partner, PendingConsent } from 'src/reducers/partner/types';

import { logAnalyticsEvent } from 'Utilities/analytics/analyticsHelper';
import { getQuickShareCodeLifetimeHours } from 'Utilities/formatHelper';
import { getUnixTimstamp } from 'Utilities/helpers';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';

import { ANALYTICS_ACTION_TYPE, ANAYLTICS_TITLE_TYPE } from 'Constants/analyticsEventTypes';
import { PARTNER_TYPES } from 'Constants/partnerTypes';

import LoadingButton from 'Components/loading/LoadingButton';
import ConfigVersionMessage from 'Components/utility/ConfigVersionMessage';

import LVDSLogo from 'Images/logos/LibreDataShare.svg';
import LVDSLogoAR from 'Images/logos/LibreDataShare_Arabic.svg';
import LVDSLogoHE from 'Images/logos/LibreDataShare_Hebrew.svg';
import LVDSLogoJA from 'Images/logos/LibreDataShare_Japanese.svg';
import LVDSLogoZH from 'Images/logos/LibreDataShare_SimplifiedChinese.svg';
import LLULogo from 'Images/logos/libreLinkUp.svg';
import LLULogoAR from 'Images/logos/libreLinkUp-ar.svg';
import LVLogo from 'Images/logos/libreView.svg';
import LVLogoAR from 'Images/logos/libreView-ar.svg';

import styles from './Home.module.scss';

interface CommonProps {
  connectAction: string;
  connectKey: string;
  connected: boolean;
  id: string;
  logo?: string;
  running?: string;
  setRunning?: React.Dispatch<React.SetStateAction<string>>;
  transGroupKey?: string;
}

interface DefaultProps extends CommonProps {
  itemType?: 'default'; // discriminate
}

interface QuickShareProps extends CommonProps {
  itemType: 'quickShare';

  descriptionText: string;
}

const generateQuickShare = (connected: boolean) => {
  if (!connected) {
    mediator.request('quickShare:generateCode').then((res: any) => {
      if (res.status === 0) {
        logAnalyticsEvent({
          action: ANALYTICS_ACTION_TYPE.CREATE_BTN_CLICKED,
          eventTitle: ANAYLTICS_TITLE_TYPE.LDS_CODE_CREATED,
        });
      }
    });
  } else {
    logAnalyticsEvent({
      action: ANALYTICS_ACTION_TYPE.MANAGED_BTN_CLICKED,

      eventTitle: ANAYLTICS_TITLE_TYPE.LDS_CODE_MANAGED,
    });
  }
};

type ItemProps = DefaultProps | QuickShareProps;

const AppListItem: React.FC<React.PropsWithChildren<ItemProps>> = ({
  connectAction,
  connectKey = 'Common.connect',
  connected,
  id,
  logo,
  running,
  setRunning,
  transGroupKey,
  ...rest
}) => {
  // Make sure to get the right description for the card based on the itemType
  let description = null;

  switch (rest.itemType) {
    case 'quickShare':
      description = (
        <span id="connectedAppDesc-text" className={styles.brandDesc}>
          {rest.descriptionText}
        </span>
      );
      break;

    default:
      description = (
        <span id="connectedAppDesc-text" className={styles.brandDesc}>
          {i18n.t(`${transGroupKey}.brandDesc`)}
        </span>
      );
      break;
  }

  const name = i18n.t(`${transGroupKey}.brandName`);

  const buttonAttributes = {
    id: 'connectedAppAction-btn',
    className: 'btn btn-outline-primary flex-shrink-0 font-weight-bold ml-3',
    href: '',
    onClick: () => {
      switch (rest.itemType) {
        case 'quickShare':
          generateQuickShare(connected);
          break;

        default:
          break;
      }

      setRunning && setRunning(id);
    },
    running: running === id,
  };

  buttonAttributes.href = connectAction;

  return (
    <div id={`connectedApp-${id}`} className="d-flex flex-column pb-2">
      <div className="d-flex justify-content-between align-items-center no-gutters mb-2">
        {logo ? (
          <div className="text-break flex-grow-0 flex-shrink-1">
            <img
              id="connectedAppLogo-img"
              className={`min-width-0 mw-100 ${styles.brandLogo}`}
              title={name}
              src={logo}
              alt={name}
            />
          </div>
        ) : (
          <span id="connectedAppLogo-text" className={styles.brandTitle}>
            {name}
          </span>
        )}

        <LoadingButton {...buttonAttributes}>
          {i18n.t(connectKey, { context: 'cap' })}
        </LoadingButton>
      </div>

      {/* Desc */}
      {description}
    </div>
  );
};

const mapState = ({
  config: {
    supportedProducts,
    features: { quickShare: quickShareSupported } = {},
    quickShareCode: quickShareCodeConfig,
  },
  llu: { connections, pending: lluPending },
  partner: { connected, pendingConnections, partners, consentsLoaded, pendingConsentsLoaded },
  practice: { pending },
  user: { country, phoneLanguage, practices, quickShare },
}: RootState) => {
  const lvSupported = supportedProducts?.libreview?.[country];

  const filteredPartners = Object.values(partners).some((partner) => {
    return shouldDisplayPartner(partner);
  });

  const displayOtherConnections =
    connected.length > 0 || pendingConnections.length > 0 || filteredPartners;

  return {
    connectedPartners: [...connected],
    consentsLoaded,
    displayOtherConnections,
    langauge: phoneLanguage,
    lluConnected: connections.length > 0 || lluPending.length > 0,
    lluSupported: supportedProducts?.librelinkup?.[country],
    lvConnected: Object.values(practices).length > 0 || Object.values(pending).length > 0,
    lvSupported,
    partners,
    pendingConnections,
    pendingConsentsLoaded,
    quickShare,
    quickShareLifetimeMinutes: quickShareCodeConfig ? quickShareCodeConfig.lifetimeMinutes : 0,
    quickShareSupported: lvSupported && quickShareSupported,
  };
};

const connector = connect(mapState);

type Props = ConnectedProps<typeof connector>;

const getConnectKeysAndActions = (
  consents: Consent[],
  partnerValues: Partner[],
  pendingConsents: PendingConsent[]
) => {
  const hasInbound = partnerValues.some(
    (p) => p.type === PARTNER_TYPES.INBOUND && shouldDisplayPartner(p)
  );

  if (consents.length || pendingConsents.length || hasInbound) {
    return {
      connectKey: 'Common.manage',
      connectAction: '/otherConnections',
    };
  }

  const hasAPI = partnerValues.some((p) => p.type === PARTNER_TYPES.API && shouldDisplayPartner(p));
  const hasCommercialAPI = partnerValues.some((p) => p.type === PARTNER_TYPES.COMMERCIAL_API);

  if (hasAPI && !hasCommercialAPI) {
    return {
      connectKey: 'Common.manage',
      connectAction: '/otherConnections',
    };
  }

  return {
    connectKey: 'Common.connect',
    connectAction: '/invitation',
  };
};

const shouldDisplayPartner = (partner: Partner) => {
  switch (partner.type) {
    case PARTNER_TYPES.COMMERCIAL_API:
      return true;
    case PARTNER_TYPES.API:
    case PARTNER_TYPES.INBOUND:
      return partner.display;
    case PARTNER_TYPES.FHIR:
      if (partner.subType !== 'mes') {
        return partner.display;
      }

    default:
      return false;
  }
};

const Home: React.FC<React.PropsWithChildren<Props>> = ({
  connectedPartners,
  consentsLoaded,
  displayOtherConnections,
  langauge,
  lluConnected,
  lluSupported,
  lvConnected,
  lvSupported,
  partners,
  pendingConnections,
  pendingConsentsLoaded,
  quickShare,
  quickShareSupported,
  quickShareLifetimeMinutes,
}) => {
  const [running, setRunning] = useState('');

  const partnerValues = Object.values(partners) as Partner[];

  const quickShareActive =
    !!quickShare?.code && !!quickShare?.expiresAt && getUnixTimstamp() < quickShare.expiresAt;

  const quickShareLifetimeHours = getQuickShareCodeLifetimeHours(quickShareLifetimeMinutes);

  // Setup an interval that checks every 200ms if a full second has passed, and if so unset the running ID
  // This will only ever happen when navigating back from somewhere else (i.e. a connected partner website)
  // Needed for managing the loading buttons on iOS
  useEffect(() => {
    let interval = 0;
    let prev = new Date().getTime();

    if (running) {
      interval = window.setInterval(() => {
        const now = new Date().getTime();
        const diff = now - prev - 200;

        if (diff > 1000) {
          setRunning('');
        }

        prev = now;
      }, 200);
    }

    return () => {
      window.clearInterval(interval);
    };
  }, [running]);

  const quickShareLogo = useMemo(() => {
    switch (langauge) {
      case 'ar-SA':
        return LVDSLogoAR;
      case 'zh-CN':
        return LVDSLogoZH;
      case 'he-IL':
        return LVDSLogoHE;
      case 'ja-JP':
        return LVDSLogoJA;
      default:
        return LVDSLogo;
    }
  }, [langauge]);

  const { connectKey, connectAction } = getConnectKeysAndActions(
    connectedPartners,
    partnerValues,
    pendingConnections
  );

  return (
    <div id="connectedAppsHome" className="d-flex flex-column">
      {/* Header */}
      <div className="row">
        <div className={styles.headerBar}>
          <span id="homeHeader-text">{i18n.t('Home.header')}</span>
        </div>
      </div>
      {!lluSupported && !lvSupported && !quickShareSupported && !displayOtherConnections && (
        <span id="noOptions-text" className="text-xl">
          {i18n.t('Home.noOptions')}
        </span>
      )}
      <div id="connectedAppsList" className={styles.sharingList}>
        {/* LV */}
        {lvSupported && (
          <AppListItem
            connectAction="/libreview"
            connectKey={lvConnected ? 'Common.manage' : 'Common.connect'}
            connected={lvConnected}
            id="libreview"
            logo={langauge.startsWith('ar') ? LVLogoAR : LVLogo}
            transGroupKey="LV"
          />
        )}

        {/* LLU */}
        {lluSupported && (
          <AppListItem
            connectAction="/librelinkup"
            connectKey={lluConnected ? 'Common.manage' : 'Common.connect'}
            connected={lluConnected}
            id="librelinkup"
            logo={langauge.startsWith('ar') ? LLULogoAR : LLULogo}
            transGroupKey="LLU"
          />
        )}

        {/* Quick Share */}
        {quickShareSupported && (
          <AppListItem
            connectAction="/quickshare"
            connectKey={quickShareActive ? 'Common.manage' : 'Common.create'}
            connected={quickShareActive}
            id="quickShare"
            logo={quickShareLogo}
            itemType="quickShare"
            transGroupKey="QuickShare"
            descriptionText={i18n.t('QuickShare.brandDesc', { number: quickShareLifetimeHours })}
          />
        )}

        {/* Other Connections */}
        {consentsLoaded && pendingConsentsLoaded && displayOtherConnections && (
          <AppListItem
            connectAction={connectAction}
            connectKey={connectKey}
            connected={true}
            id="otherConnections"
            transGroupKey="OtherConnections"
            descriptionText={i18n.t('OtherConnections.brandDesc')}
          />
        )}
      </div>
      <div className="align-self-end">
        <ConfigVersionMessage />
      </div>
    </div>
  );
};

export default connector(Home);
